/**
 * AGENT INSTALLER HEADER
 * 
 * Component responsável pelo header/perfil do agente no wizard de instalação
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/shared/components/ui/badge';
import { User, Calendar } from 'lucide-react';
import { AgentWithIntegrations } from '../../types/AgentInstaller';
import { translateDeep } from '@/shared/utils/i18nHelper';

interface AgentInstallerHeaderProps {
  agent: AgentWithIntegrations;
}

export const AgentInstallerHeader: React.FC<AgentInstallerHeaderProps> = ({
  agent
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0]; // 'pt' ou 'en'

  // Traduzir settings do agent
  const translatedSettings = useMemo(() => {
    return agent.settings ? translateDeep(agent.settings, currentLang) : agent.settings;
  }, [agent.settings, currentLang]);

  const getInitials = (name: string) => {
    const initials = name.split(' ').map((word) => word.charAt(0).toUpperCase());
    return initials.join('');
  };

  return (
    <div className="flex items-start gap-4 p-6 bg-background border-b border-border rounded-t-lg">
      <div className="relative">
        <div className="h-16 w-16 rounded-full bg-muted ring-4 ring-background shadow-lg overflow-hidden">
          {(agent.logo_url || agent.avatar_url) ? (
            <img 
              src={agent.logo_url || agent.avatar_url}
              alt={agent.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary/10 text-primary font-medium text-lg">
              {getInitials(agent.name)}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h1 className="text-2xl font-bold text-foreground">{agent.name}</h1>
          {translatedSettings?.basic?.version && (
            <Badge variant="secondary">v{translatedSettings.basic.version}</Badge>
          )}
          {translatedSettings?.basic?.type && (
            <Badge variant="outline">{translatedSettings.basic.type}</Badge>
          )}
        </div>

        {translatedSettings?.basic?.description && (
          <p className="text-muted-foreground mb-3">
            {translatedSettings.basic.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {translatedSettings?.basic?.author && (
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>Por {translatedSettings.basic.author}</span>
            </div>
          )}
          {translatedSettings?.basic?.last_published && (
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Publicado em {translatedSettings.basic.last_published}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
